import React, { useState, useEffect, useContext } from 'react';

import { saveAs } from 'file-saver';
import { AuthContext } from '../../../../context/AuthContext';
import Feedback from './Feedback';
import TodoRegisterLayer from '../../../../components/todo/TodoRegisterLayer';
const AVATAR_COLORS = [
  '#2196F3', '#9C27B0', '#FF9800', '#4CAF50', '#00BCD4',
  '#3F51B5', '#009688', '#FFC107', '#795548', '#607D8B'
];

const getAvatarColorInfo = (identifier) => {
  if (!identifier) return { bg: '#0d6efd', text: 'light' };
  const str = String(identifier);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(Math.imul(hash, 2654435761));
  const index = hash % AVATAR_COLORS.length;
  const color = AVATAR_COLORS[index];
  const isDarkText = ['#FFC107', '#FF9800', '#00BCD4', '#4CAF50'].includes(color);
  return { bg: color, text: isDarkText ? 'dark' : 'light' };
};

function CommentsTab({ tasks, categories, allTaskComments, instructions, newInstruction, setNewInstruction, handleCreateInstruction, isProjectMaster, canCreateTask, toggleDrawer, project, currentUser, onDataRefresh }) {
  const { user } = useContext(AuthContext);

  // 전역 수준의 순수 참조자 판별 (어떤 태스크의 담당자도 아니면 참조자)
  const isAssigneeForAnyTask = tasks.some(task => {
    const members = task.members || [];
    return members.some(m =>
      (m.is_manager === true || m.is_manager === 1) &&
      (String(m.user_id) === String(user?.id) ||
        String(m.user_id) === String(user?.userid) ||
        (m.user && String(m.user.userid) === String(user?.userid)) ||
        (m.user && String(m.user.id) === String(user?.id)))
    );
  });
  const isPureWatcher = !canCreateTask && !isAssigneeForAnyTask;
  const [viewMode, setViewMode] = useState('all');

  // 사용자 매핑 편의: task에 속한 모든 사용자 + PM/검토자 추출 (피드 대상자 이름 렌더링에도 사용되므로 본인 제외 금지)
  const allProjectMembers = React.useMemo(() => {
    if (!project) return [];

    // 1. 일반 멤버 수집
    let members = project.members ? project.members.map(m => m.user).filter(Boolean) : [];

    // 2. PM, 검토자도 강제 포함 (리더 제외)
    if (project.author) members.push(project.author);
    if (project.reviewer) members.push(project.reviewer);

    // 3. 중복 제거 (id 기준)
    const uniqueMembers = [];
    const seenIds = new Set();
    members.forEach(m => {
      if (m && !seenIds.has(m.id)) {
        seenIds.add(m.id);
        uniqueMembers.push(m);
      }
    });

    return uniqueMembers;
  }, [project]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  // To-Do 팝업 상태 추가
  const [todoAnchorEl, setTodoAnchorEl] = useState(null);
  const [todoSource, setTodoSource] = useState(null);

  const handleOpenTodoRegister = (e, item, task = null) => {
    e.stopPropagation();
    setTodoAnchorEl({ current: e.currentTarget });

    // item이 단일 코멘트인 경우(대댓글)와 그룹(group)인 경우를 모두 지원하도록 분기
    let contentStr = '';
    let targetId = item.id;
    if (item.items && item.items.length > 0) {
      const textItem = item.items.find(i => i.content && !i.attached_file_id);
      contentStr = textItem ? textItem.content : '';
      targetId = item.id || item.items[0].id;
    } else if (item.content && !item.attached_file_id) {
      contentStr = item.content;
    }

    setTodoSource({
      ref_type: 'pms_comment',
      ref_table: 'tdx_pms_task_comment',
      ref_id: targetId,
      add_title: project?.title || '',
      add_subtype: task ? 'task' : 'project',
      add_id1: project?.id,
      add_id2: task?.id || item.task_id || null,
      title: task?.task_name || task?.title || project?.title || '프로젝트',
      memo: contentStr,
    });
  };

  // 스크롤에 따른 우측 패널 동적 높이 관리 (기본 330px 빼기)
  const [panelOffset, setPanelOffset] = useState(330);

  useEffect(() => {
    const handleScroll = () => {
      // 현재 스크롤 위치
      const currentScroll = window.scrollY || document.documentElement.scrollTop;
      // 스크롤이 내려갈수록 빼주는 값(330)을 줄여서 패널 높이를 늘림. (최소 110까지만 뺌)
      const newOffset = Math.max(110, 330 - currentScroll);
      setPanelOffset(newOffset);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 접기/펼치기 상태 관리
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [collapsedTasks, setCollapsedTasks] = useState({});

  useEffect(() => {
    if (categories && categories.length > 0) {
      const validCats = [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).filter(cat => {
        const catTasks = tasks.filter(t => String(t.category_id) === String(cat.id));
        const forceDisable = catTasks.length === 0;
        return !forceDisable;
      });

      const firstValidCatId = validCats.length > 0 ? validCats[0].id : null;

      const initialCats = {};
      categories.forEach(cat => {
        initialCats[cat.id] = cat.id !== firstValidCatId; // 첫 번째 분류만 펼침(false), 나머지는 접음(true)
      });
      setCollapsedCategories(prev => Object.keys(prev).length === 0 ? initialCats : prev);

      const initialTasks = {};
      tasks.forEach(task => {
        initialTasks[task.id] = false; // 모든 항목을 기본적으로 펼침 (false)
      });
      setCollapsedTasks(prev => Object.keys(prev).length === 0 ? initialTasks : prev);

      setSelectedCategoryId(prev => {
        if (!prev) {
          return firstValidCatId || categories[0].id;
        }
        return prev;
      });
    }
  }, [categories, tasks, canCreateTask]);

  // 개별 보기 모드일 때 선택된 카테고리와 첫 번째 태스크만 펼치기
  useEffect(() => {
    if (viewMode === 'individual' && selectedCategoryId) {
      setCollapsedCategories(prev => ({ ...prev, [selectedCategoryId]: false }));

      const catTasks = tasks.filter(t => String(t.category_id) === String(selectedCategoryId));
      if (catTasks.length > 0) {
        setCollapsedTasks(prev => {
          const next = { ...prev };
          const firstTask = catTasks[0];
          next[firstTask.id] = false;
          // 나머지는 모두 접힘(true) 상태로 변경 (첫 번째 태스크만 펼치기)
          for (let i = 1; i < catTasks.length; i++) {
            next[catTasks[i].id] = true;
          }
          return next;
        });
      }
    }
  }, [viewMode, selectedCategoryId, tasks]);

  const toggleCategory = (catId) => {
    setCollapsedCategories(prev => {
      const isOpening = prev[catId] === true || prev[catId] === undefined;

      if (isOpening) {
        // 카테고리를 열 때 첫 번째 태스크만 함께 펼침
        const catTasks = tasks.filter(t => String(t.category_id) === String(catId));
        if (catTasks.length > 0) {
          setCollapsedTasks(taskPrev => {
            const next = { ...taskPrev };
            next[catTasks[0].id] = false;
            return next;
          });
        }
      }

      return { ...prev, [catId]: !isOpening };
    });
  };

  const toggleTask = (taskId) => {
    setCollapsedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  // 전체 펼치기 로직
  const expandAll = () => {
    setCollapsedCategories({});
    setCollapsedTasks({});
  };

  // 전체 접기 로직
  const collapseAll = () => {
    const allCatCollapsed = (categories || []).reduce((acc, cat) => ({ ...acc, [cat.id]: true }), {});
    const allTaskCollapsed = tasks.reduce((acc, task) => ({ ...acc, [task.id]: true }), {});
    setCollapsedCategories(allCatCollapsed);
    setCollapsedTasks(allTaskCollapsed);
  };

  const renderDummyTimeline = (task) => {
    const comments = (allTaskComments || []).filter(c => c.task_id === task.id);
    if (comments.length === 0) {
      return (
        <div className="ms-3 mt-2 mb-3 py-2 px-3 bg-light rounded-3 d-flex align-items-center justify-content-center gap-2 text-muted border border-secondary border-opacity-10" style={{ borderStyle: 'dashed' }}>
          <i className="far fa-comment-dots fs-7 opacity-50"></i>
          <span className="fs-8 fw-medium">작성된 코멘트가 없습니다.</span>
        </div>
      );
    }

    // 부모 코멘트와 자식(대댓글) 분리
    const rawParentComments = comments.filter(c => !c.parent_id);



    // 같은 사용자, 같은 시간대(1분 이내) 코멘트 그룹핑 로직
    const groupComments = (commentList) => {
      if (!commentList || commentList.length === 0) return [];
      const sorted = [...commentList].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const grouped = [];
      let currentGroup = { ...sorted[0], items: [sorted[0]] };

      for (let i = 1; i < sorted.length; i++) {
        const c = sorted[i];
        // 완전히 동일한 시간에 등록된 건(즉, 동시에 등록된 코멘트와 파일)만 하나로 묶음
        if (c.user_id === currentGroup.user_id && c.created_at === currentGroup.created_at) {
          currentGroup.items.push(c);
        } else {
          grouped.push(currentGroup);
          currentGroup = { ...c, items: [c] };
        }
      }
      grouped.push(currentGroup);
      return grouped;
    };

    const parentComments = groupComments(rawParentComments);

    const stripUniqueKey = (name) => {
      if (!name) return name;
      return name.replace(/_[0-9]{8}F[0-9]{6}(?=\.[^.]+$|$)/g, '');
    };

    const getFileIcon = (filename) => {
      if (!filename) return "far fa-file text-secondary";
      const ext = filename.split('.').pop().toLowerCase();
      switch (ext) {
        case 'pdf': return "far fa-file-pdf text-danger";
        case 'doc':
        case 'docx': return "far fa-file-word text-primary";
        case 'xls':
        case 'xlsx': return "far fa-file-excel text-success";
        case 'ppt':
        case 'pptx': return "far fa-file-powerpoint text-warning";
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'bmp':
        case 'webp': return "far fa-file-image text-info";
        case 'zip':
        case 'rar':
        case '7z': return "far fa-file-archive text-secondary";
        default: return "far fa-file text-secondary";
      }
    };

    const handleDownload = async (comment) => {
      if (comment.attached_file_id && comment.file) {
        try {
          let fileUrl = comment.file.file_path;
          if (!fileUrl) {
            // fallback if full URL is missing, assuming standard path
            fileUrl = `/uploads/pms/${comment.file.file_name}`;
          }
          const response = await fetch(fileUrl);
          const blob = await response.blob();
          saveAs(blob, stripUniqueKey(comment.file.file_name || 'download'));
        } catch (e) {
          console.error('다운로드 실패:', e);
          alert('파일 다운로드 중 오류가 발생했습니다.');
        }
      } else {
        alert('다운로드할 첨부파일 정보가 없습니다.');
      }
    };

    const renderActionButtons = (comment) => (
      <div className="d-flex gap-1 align-items-center opacity-0 action-buttons transition-opacity ms-2">
        <button onClick={() => handleDownload(comment)} className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="다운로드"><i className="fas fa-download"></i></button>
      </div>
    );

    const renderReplies = (parentId, rootAuthorId, depth = 1) => {
      const childReplies = comments.filter(c => c.parent_id === parentId);
      if (childReplies.length === 0) return null;

      // 오름차순(과거순) 정렬
      childReplies.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      return (
        <div className="border-top border-secondary border-opacity-10 d-flex flex-column py-2" style={{ backgroundColor: '#f0f2f5' }}>
          {childReplies.map((childItem, idx) => {
            const childName = childItem.user?.name || 'Unknown';
            const childPosition = childItem.user?.org?.position ? ` ${childItem.user.org.position}` : '';
            const childDept = childItem.user?.org?.dept_name ? ` [${childItem.user.org.dept_name}]` : '';
            const childFullName = `${childName}${childPosition}${childDept}`;
            const childDate = new Date(childItem.created_at).toLocaleString();
            const childAuthorId = childItem.user_id || childItem.user?.id || childItem.user?.userid;
            const isSameAuthor = String(childAuthorId) === String(rootAuthorId);
            const isFile = !!childItem.attached_file_id;
            
            const avatarInfo = getAvatarColorInfo(childAuthorId || childName);
            const photoPath = childItem.user?.org?.photo_path || childItem.user?.photo_path;

            return (
              <div key={childItem.id} className="position-relative d-flex flex-column" style={{ marginLeft: `${depth > 1 ? 15 : 0}px` }}>
                {isFile ? (
                  <div className="d-flex align-items-center w-100 action-container my-1 px-2">
                    <i className="fas fa-reply fa-rotate-180 text-secondary me-2 fs-7"></i>
                    <div className="d-flex align-items-center bg-white border border-secondary border-opacity-10 rounded-pill px-2 py-1 shadow-sm flex-grow-1">
                      <div className="d-flex align-items-center" style={{ cursor: 'pointer', overflow: 'hidden' }} onClick={() => handleDownload(childItem)}>
                        <i className={`${getFileIcon(childItem.file?.file_name || childItem.content)} me-2 fs-6 opacity-75 flex-shrink-0`}></i>
                        <span
                          className="fs-8 text-dark fw-bold me-2 text-truncate"
                          style={{ maxWidth: `calc(min(350px, 20vw) - ${(depth > 1 ? (depth - 1) * 15 : 0)}px)` }}
                          onMouseEnter={(e) => {
                            if (e.target.offsetWidth < e.target.scrollWidth) {
                              e.target.title = stripUniqueKey(childItem.file?.file_name || childItem.content);
                            } else {
                              e.target.title = '';
                            }
                          }}
                        >
                          {stripUniqueKey(childItem.file?.file_name || childItem.content)}
                        </span>
                        <span className="fs-8 text-secondary opacity-75 flex-shrink-0 me-3">[{Math.round((childItem.file?.file_size || 1024) / 1024)}kb]</span>
                      </div>
                      {!isSameAuthor && <span className="text-secondary me-2 flex-shrink-0" style={{ fontSize: '11px' }}>{childFullName}</span>}
                      <span className="text-muted flex-shrink-0" style={{ fontSize: '11px' }}>{childDate}</span>
                      <div className="ms-1 d-flex align-items-center flex-shrink-0">
                        {renderActionButtons(childItem)}
                      </div>
                      <div className="flex-grow-1"></div>
                    </div>
                  </div>
                ) : (
                  <div className="position-relative d-flex w-100 my-1" style={{ paddingLeft: '14px', marginLeft: '10px' }}>
                    {/* L자 꺾쇠 연결선 (CSS border) */}
                    <div className="position-absolute" style={{ left: '0', top: '0', width: '14px', height: '16px', borderLeft: '2px solid rgba(0,0,0,0.1)', borderBottom: '2px solid rgba(0,0,0,0.1)', borderBottomLeftRadius: '6px' }}></div>
                    {/* 아바타 */}
                    <div className="rounded-circle d-flex align-items-center justify-content-center shadow-sm fw-bold fs-9 overflow-hidden flex-shrink-0"
                      style={{ width: '22px', height: '22px', backgroundColor: photoPath ? 'transparent' : avatarInfo.bg, color: avatarInfo.text === 'dark' ? '#333' : '#fff', marginTop: '2px' }}>
                      {photoPath ? (
                        <img src={photoPath} alt={childName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        childName !== 'Unknown' ? childName.charAt(0) : '?'
                      )}
                    </div>
                    {/* 이름 + 내용 */}
                    <div className="ms-2 flex-grow-1 min-w-0">
                      <div className="d-flex align-items-center mb-1 flex-wrap">
                        <span className="fw-bold text-dark fs-8 me-2">{childFullName}</span>
                        <span className="text-muted" style={{ fontSize: '11px' }}>{childDate}</span>
                      </div>
                      <div className="text-dark custom-scrollbar w-100" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '150px', overflowY: 'auto', fontSize: '12px', lineHeight: '1.4' }}>
                        {childItem.target_mention && <span className="text-primary fw-bold me-1">@{childItem.target_mention}</span>}
                        {childItem.content}
                      </div>
                    </div>
                  </div>
                )}
                {/* 대댓글 재귀 호출 */}
                {renderReplies(childItem.id, rootAuthorId, depth + 1)}
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <div className="position-relative ms-3 mt-3 pb-1">
        {parentComments.map((group, index) => {
          const isLast = index === parentComments.length - 1;
          const firstItem = group.items[0];
          const authorUserId = firstItem.user_id || firstItem.user?.id || firstItem.user?.userid;
          const authorName = firstItem.user?.name || 'Unknown';
          const authorPosition = firstItem.user?.org?.position ? ` ${firstItem.user.org.position}` : '';
          const authorDept = firstItem.user?.org?.dept_name ? ` [${firstItem.user.org.dept_name}]` : '';
          const authorFullName = `${authorName}${authorPosition}${authorDept}`;
          const dateStr = new Date(firstItem.created_at).toLocaleString();
          const avatarInfo = getAvatarColorInfo(authorUserId || authorName);
          const photoPath = firstItem.user?.org?.photo_path || firstItem.user?.photo_path;

          // 대댓글은 각 개별 아이템 아래에 바로 붙이기 위해 여기서의 그룹핑은 제거함

          return (
            <div key={group.id} className={`position-relative comment-item-group ${isLast ? 'mb-0' : 'mb-3'}`} style={{ paddingLeft: '28px' }}>
              {/* 타임라인 세로 선 */}
              {!isLast && (
                <div className="position-absolute bg-secondary bg-opacity-25" style={{ width: '2px', top: '36px', bottom: '-22px', left: '0px', zIndex: 0 }}></div>
              )}

              {/* 트렌디한 아바타 점 (작성자 이니셜) */}
              <div className="position-absolute avatar-wrapper" style={{ left: '-15px', top: '4px', zIndex: 10 }}>
                <div className="rounded-circle d-flex align-items-center justify-content-center shadow-sm fw-bold fs-7 overflow-hidden"
                  style={{ width: '32px', height: '32px', backgroundColor: photoPath ? 'transparent' : avatarInfo.bg, color: avatarInfo.text === 'dark' ? '#333' : '#fff' }}>
                  {photoPath ? <img src={photoPath} alt={authorName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (authorName !== 'Unknown' ? authorName.charAt(0) : '?')}
                </div>
              </div>

              {/* 모던 융합 말풍선 (텍스트 + 파일 + 대댓글 모두 포함된 단일 카드) */}
              <div className="bg-white rounded-4 shadow-sm position-relative w-100 border border-secondary border-opacity-10" style={{ borderTopLeftRadius: '4px' }}>
                <div className="action-container">
                  <div className="px-3 py-2 border-bottom border-secondary border-opacity-10 bg-light d-flex align-items-center position-relative" style={{ borderTopRightRadius: '0.5rem', borderTopLeftRadius: '4px' }}>
                    <span className="fw-bold text-dark fs-7 me-2">{authorFullName}</span>
                    <span className="text-muted fs-8 me-auto">{dateStr}</span>
                    {group.items.some(item => item.content && (!item.attached_file_id || (item.content !== item.file?.file_name && item.content !== stripUniqueKey(item.file?.file_name)))) && (
                      <div className="opacity-0 action-buttons transition-opacity" style={{ position: 'relative' }}>
                        <button
                          className="btn btn-link p-1 text-secondary hover-info border-0 shadow-none ms-2"
                          onClick={(e) => handleOpenTodoRegister(e, group, task)}
                          title="To-Do 등록"
                        >
                          <i className="fas fa-tasks fs-7"></i>
                        </button>
                        {todoSource && (todoSource.ref_id === group.id || todoSource.ref_id === group.items[0].id) && (
                          <TodoRegisterLayer
                            source={todoSource}
                            onClose={() => {
                              setTodoSource(null);
                              todoAnchorEl.current = null;
                            }}

                            anchorRef={todoAnchorEl}
                            direction="down"
                            align="right"
                          />
                        )}
                      </div>
                    )}
                  </div>

                  {/* 텍스트 영역 */}
                  <div className="d-flex flex-column">
                    {group.items.filter(item => item.content && (!item.attached_file_id || (item.content !== item.file?.file_name && item.content !== stripUniqueKey(item.file?.file_name)))).map(item => (
                      <React.Fragment key={`text-wrap-${item.id}`}>
                        <div className="text-dark lh-base px-3 py-2" style={{ fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '105px', overflowY: 'auto' }}>
                          {item.content}
                        </div>
                        {comments.some(c => c.parent_id === item.id) && (
                          renderReplies(item.id, authorUserId)
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* 첨부파일 영역 */}
                <div className="d-flex flex-column">
                  {(() => {
                    const fileItems = group.items.filter(item => item.attached_file_id);
                    const originalFiles = fileItems.filter(item => !item.file?.created_at || new Date(item.file.created_at).getTime() - new Date(group.created_at).getTime() <= 60000);
                    const addedFiles = fileItems.filter(item => item.file?.created_at && new Date(item.file.created_at).getTime() - new Date(group.created_at).getTime() > 60000);
                    addedFiles.sort((a, b) => new Date(b.file.created_at) - new Date(a.file.created_at));
                    const sortedFileItems = [...originalFiles, ...addedFiles];

                    return sortedFileItems.map((item, fIdx) => (
                      <React.Fragment key={`file-wrap-${item.id}`}>
                        <div className={`d-flex align-items-center action-container px-2 py-1 bg-primary bg-opacity-10 ${fIdx === 0 ? 'border-top border-primary border-opacity-10' : 'border-top border-white border-opacity-50'}`}>
                          <div className="d-flex align-items-center justify-content-center bg-white rounded-circle me-2 flex-shrink-0 shadow-sm" style={{ width: '28px', height: '28px' }}>
                            <i className={`${getFileIcon(item.file?.file_name || item.content)} fs-6`}></i>
                          </div>
                          <span
                            className="fs-7 text-primary fw-bold me-2 text-truncate"
                            style={{ maxWidth: '250px', cursor: 'pointer' }}
                            onClick={() => handleDownload(item)}
                            onMouseEnter={(e) => {
                              if (e.target.offsetWidth < e.target.scrollWidth) {
                                e.target.title = stripUniqueKey(item.file?.file_name || item.content || '첨부파일');
                              } else {
                                e.target.title = '';
                              }
                            }}
                          >
                            {stripUniqueKey(item.file?.file_name || item.content || '첨부파일')}
                          </span>
                          <span className="fs-8 text-primary opacity-75 flex-shrink-0">[{Math.round((item.file?.file_size || 0) / 1024)}kb]</span>
                          <div className="ms-2 d-flex align-items-center">
                            {item.file?.created_at && new Date(item.file.created_at).getTime() - new Date(group.created_at).getTime() > 60000 && (() => {
                              const itemAuthorId = item.user_id || item.user?.id || item.user?.userid;
                              const isSameAuthor = String(itemAuthorId) === String(authorUserId);
                              const itemName = item.user?.name || 'Unknown';
                              const itemPos = item.user?.org?.position ? ` ${item.user.org.position}` : '';
                              const itemDept = item.user?.org?.dept_name ? ` [${item.user.org.dept_name}]` : '';
                              const itemFullName = `${itemName}${itemPos}${itemDept}`;
                              return (
                                <span className="fs-8 text-secondary opacity-75 me-2 flex-shrink-0" style={{ fontSize: '11px' }}>
                                  {isSameAuthor ? `(추가됨: ${new Date(item.file.created_at).toLocaleString()})` : `(추가: ${itemFullName} / ${new Date(item.file.created_at).toLocaleString()})`}
                                </span>
                              );
                            })()}
                            {renderActionButtons(item)}
                          </div>
                        </div>
                        {comments.some(c => c.parent_id === item.id) && (
                          renderReplies(item.id, authorUserId, 2)
                        )}
                      </React.Fragment>
                    ));
                  })()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const handleViewModeChange = (mode) => {
    if (viewMode === mode) return;
    setViewMode(mode);

    const validCats = categories ? [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).filter(cat => {
      const catTasks = tasks.filter(t => String(t.category_id) === String(cat.id));
      const forceDisable = catTasks.length === 0;
      return !forceDisable;
    }) : [];

    const firstValidCatId = validCats.length > 0 ? validCats[0].id : null;

    const initialCats = {};
    categories && categories.forEach(cat => {
      if (String(cat.id) === String(firstValidCatId)) {
        initialCats[cat.id] = false;
      } else {
        initialCats[cat.id] = true;
      }
    });
    setCollapsedCategories(initialCats);

    const initialTasks = {};
    let isFirstInViewChange = true;
    tasks.forEach(task => {
      if (String(task.category_id) === String(firstValidCatId)) {
        if (isFirstInViewChange) {
          initialTasks[task.id] = false;
          isFirstInViewChange = false;
        } else {
          initialTasks[task.id] = true;
        }
      } else {
        initialTasks[task.id] = true;
      }
    });
    setCollapsedTasks(initialTasks);

    if (firstValidCatId) {
      setSelectedCategoryId(firstValidCatId);
    }
  };

  return (
    <div className="row m-0 animation-fade-in gap-3 align-items-start">
      {/* 컴포넌트 내장 CSS: 꼬리표(Tail) 디자인 및 호버 등 */}
      <style>{`
        .hover-bg-gray:hover { background-color: #e9ecef !important; }
        .action-container:hover .action-buttons { opacity: 1 !important; }
        .transition-opacity { transition: opacity 0.2s ease; }
        .avatar-wrapper { position: relative; display: inline-block; }
        .speech-bubble { position: relative; }
        .speech-bubble::before, .speech-bubble::after {
          content: '';
          position: absolute;
          top: 10px;
          width: 0;
          height: 0;
          border-style: solid;
        }
        /* 흰색 박스 (border 있음) 꼬리표 */
        .speech-bubble-white::before {
          left: -9px;
          border-width: 8px 9px 8px 0;
          border-color: transparent #dee2e6 transparent transparent;
        }
        .speech-bubble-white::after {
          left: -8px;
          border-width: 8px 9px 8px 0;
          border-color: transparent #ffffff transparent transparent;
        }
        /* 연한 회색 박스 (border 없음) 꼬리표 */
        .speech-bubble-light::before {
          display: none; /* 불필요한 테두리 가상요소 숨김 (까만 점 버그 픽스) */
        }
        .speech-bubble-light::after {
          left: -8px;
          border-width: 8px 9px 8px 0;
          border-color: transparent var(--bs-light, #f8f9fa) transparent transparent;
        }
        /* 부모 컨테이너들의 overflow 충돌 강제 해제 (브라우저 스크롤 기준 sticky 동작 보장) */
        .container-fluid {
          overflow-x: visible !important;
          overflow-y: visible !important;
        }
        #pms-main-scroll {
          overflow: visible !important;
        }
        /* 라디오 토글 커스텀 색상 */
        .btn-custom-toggle { color: #495057; border: 1px solid #ced4da; background-color: #fff; }
        .btn-custom-toggle:hover { color: #4c6ef5 !important; border-color: #4c6ef5 !important; background-color: #fff !important; }
        .btn-check:checked + .btn-custom-toggle { background-color: #4c6ef5 !important; border-color: #4c6ef5 !important; color: #fff !important; }
        .btn-check:checked + .btn-custom-toggle:hover { background-color: #3b5bdb !important; border-color: #3b5bdb !important; color: #fff !important; }
        /* 태스크 이름 클릭 가능 표시 */
        .task-name-hover:hover { color: #4c6ef5 !important; }
      `}</style>

      {/* 🧩 좌측: 전체 코멘트 타임라인 피드 */}
      <div className="col bg-white rounded shadow-sm border p-4">

        {/* 상단 컨트롤 영역 (통합 보기 / 개별 보기) */}
        <div className="d-flex justify-content-between align-items-center border-bottom border-secondary border-opacity-25 pb-1 mb-2">
          <div className="btn-group" role="group">
            <input type="radio" className="btn-check" id="commentViewModeAll" checked={viewMode === 'all'} onChange={() => handleViewModeChange('all')} />
            <label className="btn btn-custom-toggle px-3 fw-bold" htmlFor="commentViewModeAll">
              <i className="fas fa-list-ul me-1"></i> 통합 보기
            </label>

            <input type="radio" className="btn-check" id="commentViewModeInd" checked={viewMode === 'individual'} onChange={() => handleViewModeChange('individual')} />
            <label className="btn btn-custom-toggle px-3 fw-bold" htmlFor="commentViewModeInd">
              <i className="fas fa-file-alt me-1"></i> 개별 보기
            </label>
          </div>

          <div className="d-flex align-items-center">
            {viewMode === 'all' ? (
              tasks.length > 0 ? (
                <div className="d-flex gap-2">
                  <button className="btn btn-outline-primary btn-sm fw-bold d-flex align-items-center shadow-sm" onClick={expandAll} style={{ padding: '4px 10px' }}>
                    <i className="fas fa-angle-double-down me-2"></i> 전체 펼치기
                  </button>
                  <button className="btn btn-outline-primary btn-sm fw-bold d-flex align-items-center shadow-sm" onClick={collapseAll} style={{ padding: '4px 10px' }}>
                    <i className="fas fa-angle-double-up me-2"></i> 전체 접기
                  </button>
                </div>
              ) : null
            ) : (
              <div className="d-flex align-items-center">
                <span className="fw-bold text-secondary me-2">분류 :</span>
                <select
                  className="form-select shadow-sm fw-bold text-dark text-truncate"
                  style={{ width: '300px', borderColor: '#ced4da' }}
                  value={selectedCategoryId || ''}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                >
                  {categories && categories.length > 0 ? (
                    [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(cat => {
                      const catTasks = tasks.filter(t => String(t.category_id) === String(cat.id));
                      const forceDisable = catTasks.length === 0;
                      return (
                        <option key={cat.id} value={cat.id} title={cat.name} disabled={forceDisable}>
                          {cat.name} {forceDisable ? '(세부항목 없음)' : ''}
                        </option>
                      );
                    })
                  ) : (<option value="">등록된 분류가 없습니다</option>
                  )}
                </select>
              </div>
            )}
          </div>
        </div>

        {(viewMode === 'all' && categories ? [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)) : (categories || []).filter(c => String(c.id) === String(selectedCategoryId))).map(cat => {
          const catTasks = tasks.filter(t => String(t.category_id) === String(cat.id));
          const forceDisable = catTasks.length === 0;

          const catAllItems = catTasks.reduce((acc, task) => acc.concat((allTaskComments || []).filter(c => c.task_id === task.id)), []);
          const catCommentsCount = catAllItems.filter(c => !c.attached_file_id).length;
          const catFilesCount = catAllItems.filter(c => c.attached_file_id).length;
          const isCatExpanded = forceDisable ? false : (collapsedCategories[cat.id] !== true);

          return (
            <div key={cat.id} className="mb-4">
              {/* 카테고리 헤더 (통합 보기에서만 렌더링) */}
              {viewMode === 'all' && (
                <div
                  className="d-flex align-items-center mb-2"
                  style={{ cursor: forceDisable ? 'default' : 'pointer', opacity: forceDisable ? 0.6 : 1 }}
                  onClick={() => {
                    if (!forceDisable) toggleCategory(cat.id);
                  }}
                >
                  <div style={{ width: '20px', textAlign: 'center' }}>
                    <i className={`fas fa-caret-${isCatExpanded ? 'down' : 'right'} text-secondary`} style={{ fontSize: '1.2rem', opacity: forceDisable ? 0.5 : 1 }}></i>
                  </div>
                  <i className="fas fa-layer-group text-primary ms-1 me-2" style={{ fontSize: '1.1rem', opacity: forceDisable ? 0.5 : 1 }}></i>
                  <span className="fw-bolder fs-5 text-dark" style={{ textDecoration: 'none' }}>{cat.name}</span>
                  <span className="badge bg-light text-secondary ms-2 border px-2 py-1 fs-8" style={{ opacity: forceDisable ? 0.5 : 1 }}>
                    {catTasks.length} 항목
                    {catCommentsCount > 0 && ` / ${catCommentsCount} 코멘트`}
                    {catFilesCount > 0 && ` / ${catFilesCount} 파일`}
                  </span>
                </div>
              )}

              {/* 해당 카테고리 안의 태스크들 */}
              {(viewMode === 'individual' || (isCatExpanded && catTasks.length > 0)) && (
                <div className={viewMode === 'all' ? "ps-3 ms-2 border-start border-2 border-light" : ""}>
                  {catTasks.map(task => {
                    const taskCommentsCount = (allTaskComments || []).filter(c => c.task_id === task.id).length;
                    return (
                      <div key={task.id} className="mb-3">
                        {/* 프리미엄 Task 헤더 카드 */}
                        <div
                          className="d-flex align-items-center rounded-3 mb-2 border border-secondary border-opacity-10 bg-white"
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                          onClick={() => toggleTask(task.id)}
                        >
                          <div className={`d-flex align-items-center justify-content-center rounded-circle me-3 ${collapsedTasks[task.id] ? 'bg-primary bg-opacity-10 text-primary' : 'bg-primary bg-opacity-10 text-primary'}`} style={{ width: '24px', height: '24px', transition: 'all 0.2s' }}>
                            <i className={`fas fa-chevron-${collapsedTasks[task.id] ? 'right' : 'down'} fs-8`}></i>
                          </div>
                          <span
                            className={`fw-bolder flex-grow-1 text-dark text-truncate ${!isPureWatcher && toggleDrawer ? 'task-name-hover' : ''}`}
                            style={{ fontSize: '14px', cursor: (!isPureWatcher && toggleDrawer) ? 'pointer' : 'default', transition: 'color 0.15s' }}
                            onClick={(e) => {
                              if (!isPureWatcher && toggleDrawer) {
                                e.stopPropagation();
                                toggleDrawer(task, 'comments');
                              }
                            }}
                          >
                            {task.title}
                          </span>
                          <span className={`badge rounded-pill fs-7 px-2 py-1 ms-2 ${taskCommentsCount > 0 ? 'bg-primary text-white shadow-sm' : 'bg-white text-secondary border border-primary border-opacity-25'}`}>
                            {taskCommentsCount}
                          </span>
                        </div>

                        {/* 타임라인 본문 (접혀있지 않을 때만 표시) */}
                        {!collapsedTasks[task.id] && renderDummyTimeline(task)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {(!categories || categories.length === 0 || tasks.length === 0) && (
          <div className="text-center text-muted py-5 mt-5">
            <i className="far fa-folder-open fa-3x mb-3 text-light"></i>
            <p>등록된 세부항목이 없습니다.</p>
          </div>
        )}
      </div>

      {/* 가운데 구분선 */}
      <div className="d-none d-md-block" style={{ width: '1px', backgroundColor: '#e9ecef', padding: 0 }}></div>

      {/* 🧩 우측: 작업지시 게시판 (프리미엄 UI 개편) */}
      <div className="col-md-5 bg-white rounded-4 shadow border-0 d-flex flex-column p-0 overflow-hidden" style={{ position: 'sticky', top: '90px', height: `calc(100vh - ${panelOffset}px)`, minHeight: '500px' }}>
        <Feedback
          project={project}
          currentUser={currentUser}
          allProjectMembers={allProjectMembers}
          isPureWatcher={isPureWatcher}
          onDataRefresh={onDataRefresh}
        />
      </div>
    </div>
  );
}

export default CommentsTab;
